import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles, BookOpen, Users, ShieldCheck, GraduationCap, TreePine, Library, Trophy } from "lucide-react";
import { BrandWord } from "@/components/brand";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "MAKAI — AI-powered learning for Uganda" },
      { name: "description", content: "Lessons, quizzes, a digital library and the Learning Tree for learners, parents, teachers and schools across Uganda." },
      { property: "og:title", content: "MAKAI — AI-powered learning for Uganda" },
      { property: "og:description", content: "Lessons, quizzes, a digital library and the Learning Tree for learners, parents, teachers and schools across Uganda." },
    ],
  }),
  component: Landing,
});

function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 lg:px-8">
        <BrandWord />
        <nav className="hidden items-center gap-7 text-sm font-medium text-muted-foreground md:flex">
          <a href="#features" className="hover:text-foreground">Features</a>
          <a href="#roles" className="hover:text-foreground">For everyone</a>
          <a href="#tree" className="hover:text-foreground">Learning Tree</a>
        </nav>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/auth">Sign in</Link>
          </Button>
          <Button asChild size="sm" className="bg-primary hover:bg-primary/90">
            <Link to="/auth">Get started</Link>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pattern-kente absolute inset-0 opacity-60" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pt-10 pb-16 lg:grid-cols-2 lg:px-8 lg:pt-16 lg:pb-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> AI-powered learning, made in Uganda
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Where every Ugandan learner
              <span className="block bg-gradient-hero bg-clip-text text-transparent">grows their tree.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              MAKAI brings the Ugandan curriculum to life with adaptive lessons,
              quizzes, a rich digital library and the Learning Tree — so learners,
              parents, teachers and schools all grow together.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                <Link to="/auth">Start learning <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#roles">Explore roles</a>
              </Button>
            </div>
            <dl className="mt-10 grid max-w-md grid-cols-3 gap-4">
              {[
                { k: "1,200+", v: "Lessons" },
                { k: "P1 – S6", v: "Grades" },
                { k: "4", v: "Roles" },
              ].map((s) => (
                <div key={s.v}>
                  <dt className="font-display text-2xl font-bold text-primary">{s.k}</dt>
                  <dd className="text-xs text-muted-foreground">{s.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Hero illustration card */}
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-gradient-hero opacity-25 blur-2xl" aria-hidden />
            <div className="relative rounded-3xl border border-border bg-card p-5 shadow-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Today's lesson</div>
                  <div className="font-display text-lg font-bold">Multiplication tables</div>
                </div>
                <span className="rounded-full bg-gold/20 px-3 py-1 text-xs font-semibold text-gold-foreground">P4 · Math</span>
              </div>

              <div className="mt-5 rounded-2xl bg-gradient-card p-5">
                <div className="flex items-center gap-3">
                  <TreePine className="h-8 w-8 text-primary" />
                  <div>
                    <div className="text-sm font-semibold">Your Learning Tree</div>
                    <div className="text-xs text-muted-foreground">3 of 7 branches grown this week</div>
                  </div>
                </div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-background">
                  <div className="h-full w-[58%] rounded-full bg-gradient-hero" />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[11px] font-semibold">
                  {["Numeracy","Literacy","Discovery"].map((b) => (
                    <div key={b} className="rounded-lg bg-background/80 px-2 py-2 text-muted-foreground">{b}</div>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { l: "Streak", v: "6 days", c: "text-primary" },
                  { l: "XP", v: "1,240", c: "text-gold-foreground" },
                  { l: "Badges", v: "12", c: "text-sky" },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl border border-border p-3">
                    <div className="text-[11px] text-muted-foreground">{s.l}</div>
                    <div className={`font-display text-lg font-bold ${s.c}`}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">Everything a learner needs to thrive</h2>
          <p className="mt-3 text-muted-foreground">Designed around the Ugandan curriculum and adapted to each learner's age and grade.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <Link
              key={f.title}
              to={f.to}
              className="group rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
            >
              <div className={`mb-4 grid h-11 w-11 place-items-center rounded-xl ${f.bg}`}>
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-bold">{f.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
              <div className="mt-4 inline-flex items-center text-sm font-semibold text-primary">
                {f.cta} <ArrowRight className="ml-1 h-4 w-4 transition group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="bg-secondary/60">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">One platform, four experiences</h2>
            <p className="mt-3 text-muted-foreground">Pick your role and we'll tailor the experience — including an adaptive interface that grows from P1 to S6.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {ROLES.map((r) => (
              <Link
                key={r.title}
                to="/auth"
                className="group rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
              >
                <div className={`mb-4 grid h-11 w-11 place-items-center rounded-xl ${r.bg}`}>
                  <r.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-bold">{r.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
                <div className="mt-4 inline-flex items-center text-sm font-semibold text-primary">
                  Open dashboard <ArrowRight className="ml-1 h-4 w-4 transition group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tree CTA */}
      <section id="tree" className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-border bg-card p-8 text-foreground shadow-card sm:p-12">
          <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">
                <TreePine className="h-3.5 w-3.5" /> Learning Tree
              </span>
              <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">Grow what you know, branch by branch.</h2>
              <p className="mt-3 max-w-xl text-muted-foreground">
                Every lesson, quiz and library book grows a learner's tree across Numeracy,
                Literacy, Discovery and Expression — a beautiful map of progress they actually own.
              </p>
              <Button asChild size="lg" className="mt-6 bg-gold text-gold-foreground hover:bg-gold/90">
                <Link to="/auth">Create your account</Link>
              </Button>
            </div>
            <div className="rounded-2xl border border-border bg-muted/40 p-5">
              <div className="grid grid-cols-2 gap-3">
                {["Numeracy","Literacy","Discovery","Expression"].map((b, i) => (
                  <div key={b} className="rounded-xl border border-border bg-background p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{b}</div>
                    <div className="mt-2 font-display text-2xl font-bold text-foreground">{["68%","82%","45%","73%"][i]}</div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-gold" style={{ width: ["68%","82%","45%","73%"][i] }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-4 py-8 sm:flex-row sm:items-center lg:px-8">
          <BrandWord />
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} MAKAI. Proudly built for Uganda.</p>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  { title: "Adaptive lessons", body: "Bite-size, curriculum-aligned lessons that adapt from P1 playfulness to S6 depth.", icon: BookOpen, bg: "bg-primary-soft text-primary", to: "/learner/lessons", cta: "Open lessons" },
  { title: "Smart quizzes", body: "Practice and exam-style quizzes with instant feedback and revision tips.", icon: Trophy, bg: "bg-gold/20 text-gold-foreground", to: "/learner/quizzes", cta: "Start a quiz" },
  { title: "Digital library", body: "Books, audio and past papers — including UNEB practice for PLE, UCE and UACE.", icon: Library, bg: "bg-sky/15 text-sky", to: "/learner/library", cta: "Browse library" },
  { title: "Learning Tree", body: "A living map of every learner's growth across four branches of knowledge.", icon: TreePine, bg: "bg-primary-soft text-primary", to: "/learner/tree", cta: "View your tree" },
  { title: "Parent linking", body: "Parents securely link to their children and follow progress in real time.", icon: ShieldCheck, bg: "bg-accent text-accent-foreground", to: "/parent", cta: "Open parent dashboard" },
  { title: "Class management", body: "Teachers run classes, assign work and see exactly who needs support.", icon: Users, bg: "bg-gold/20 text-gold-foreground", to: "/teacher", cta: "Open teacher dashboard" },
] as const;

const ROLES = [
  { title: "Learner", body: "Lessons, quizzes, library and the Learning Tree, tuned to your grade.", icon: GraduationCap, bg: "bg-primary-soft text-primary" },
  { title: "Parent", body: "Link your children, follow weekly progress and celebrate streaks.", icon: ShieldCheck, bg: "bg-sky/15 text-sky" },
  { title: "Teacher", body: "Manage classes, assign content and see analytics that matter.", icon: Users, bg: "bg-gold/20 text-gold-foreground" },
  { title: "School Admin", body: "Run your school, manage teachers and monitor outcomes.", icon: BookOpen, bg: "bg-accent text-accent-foreground" },
];
